import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { AxiosError } from 'axios';

import { INNER_ROUTES } from '@app/dashboard/constants/inner-routes';
import { Breadcrumbs, Button } from '@unified-checkout/design-system';
import { Xmark } from '@unified-checkout/design-system/icons/index';

import type { ISingleDetailsHeaderProps } from '@app/dashboard/types/innerLayout/risk_center/single-details';

import {
  ACTIONS,
  RISK_CENTER_TYPES,
} from '@app/dashboard/constants/risk_center';
import { SYSTEM_EVENTS_SRC_FEATURES } from '@app/dashboard/constants/tracking_events/system';
import type {
  TAddEditPayload,
  TDeleteFn,
  TEditFn,
  TRiskCenterTypes,
} from '@app/dashboard/types/innerLayout/risk_center';
import { onToasterShowHandler } from '@app/dashboard/utils/tracking/onToasterShowHandler';
import { getMerchantId } from '@app/dashboard/utils/localStorageUtils';
import { notify } from '@app/dashboard/utils/notifier';

import { getListTypeValue } from '../../utils/modals';
import { ConfirmationModal } from '../../Modals/ConfirmationModal';
import AddEditModal from '../../Modals/AddEditModal';

import styles from './Header.module.scss';

const Header = ({
  riskCenterData,
  modalAction,
  modalsConfig,
  setModalAction,
}: ISingleDetailsHeaderProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id, type } = useParams<{ id: string; type: TRiskCenterTypes }>();

  const [isModalOpen, setIsModalOpen] = useState({
    edit: false,
    delete: false,
    activateDeactivate: false,
  });

  const { mutate: mutateRiskCenter, isPending: isMutating } = useMutation({
    mutationFn: () => {
      const queryFn = modalsConfig?.API_MAPPER;

      if (modalAction && modalAction === ACTIONS.DELETE) {
        return (queryFn as TDeleteFn)(Number(id));
      } else {
        let payload: TAddEditPayload;
        const basePayload = {
          active: modalAction === ACTIONS.ACTIVATE,
          merchant: getMerchantId(),
        };

        if (type === RISK_CENTER_TYPES.BLACKLIST) {
          payload = {
            ...basePayload,
            reason: riskCenterData?.reason || '',
            blacklist_data: riskCenterData?.blacklist_data || [],
            blacklist_type:
              getListTypeValue(riskCenterData?.blacklist_type) || '',
          };
        } else if (type === RISK_CENTER_TYPES.WHITELIST) {
          payload = {
            ...basePayload,
            reason: riskCenterData?.reason || '',
            whitelist_data: riskCenterData?.whitelist_data || [],
            whitelist_type:
              getListTypeValue(riskCenterData?.whitelist_type) || '',
          };
        } else {
          payload = {
            ...basePayload,
            rule: riskCenterData?.rule?.id || '',
            priority: riskCenterData?.priority || 1,
          };
        }

        return (queryFn as TEditFn)(Number(id), payload);
      }
    },
    onSuccess: () => {
      notify.success(t(modalsConfig?.SUCCESS_MESSAGE) || '', {
        onShowHandler: () =>
          onToasterShowHandler({
            type: 'success',
            trackingCode: modalsConfig?.TRACKING_CODE,
            msg: t(modalsConfig?.SUCCESS_MESSAGE),
            srcFeature: SYSTEM_EVENTS_SRC_FEATURES.RISK_CENTER.DETAILS,
          }),
      });

      setTimeout(() => {
        setIsModalOpen({
          edit: false,
          delete: false,
          activateDeactivate: false,
        });
        setModalAction(null);
        navigate(INNER_ROUTES.RISK_CENTER);
      }, 2000);
    },
    onError: (axiosErr: AxiosError<{ message?: string }>) => {
      let message = t('errors.somethingWentWrong');

      if (axiosErr.response?.data?.message) {
        message = axiosErr.response?.data?.message;
      } else if (axiosErr.message) {
        message = axiosErr.message;
      }

      notify.error(message, {
        onShowHandler: () =>
          onToasterShowHandler({
            type: 'error',
            trackingCode: modalsConfig?.TRACKING_CODE,
            msg: message,
            srcFeature: SYSTEM_EVENTS_SRC_FEATURES.RISK_CENTER.DETAILS,
          }),
      });
    },
  });

  return (
    <div className={styles['single-risk-center__header']}>
      <Breadcrumbs
        linksProps={[
          {
            text: t('innerLayout.navbar.riskCenter'),
            onClickHandler: () => navigate(INNER_ROUTES.RISK_CENTER),
            active: true,
          },
          {
            text: t(`innerLayout.riskCenter.${type}.singleDetails.id`, {
              id,
            }),
          },
        ]}
      />

      {!!modalAction && (
        <ConfirmationModal
          actionType={modalAction}
          modalContent={{
            title: modalsConfig?.TITLE || '',
            supportingText: modalsConfig?.SUBTITLE || '',
            modalState: 'primary',
          }}
          open={
            isModalOpen[
              modalAction === ACTIONS.DELETE ? 'delete' : 'activateDeactivate'
            ]
          }
          primaryBtn={{
            text: modalsConfig?.CONFIRMATION_BTN || '',
            color: modalAction === ACTIONS.DELETE ? 'danger' : 'primary',
            onClick: mutateRiskCenter,
            loading: isMutating,
            disabled: isMutating,
          }}
          secondaryBtn={{
            text: t('form.cancel'),
            onClick: () => {
              setIsModalOpen((prev) => ({
                ...prev,
                delete: false,
                activateDeactivate: false,
              }));
              setModalAction(null);
            },
            disabled: isMutating,
          }}
        />
      )}

      {type !== RISK_CENTER_TYPES.RULES && (
        <AddEditModal
          currentTab={type as TRiskCenterTypes}
          isModalOpen={isModalOpen.edit}
          setIsShareModalOpen={(open: boolean) => {
            setIsModalOpen((prev) => ({ ...prev, edit: open }));
          }}
          action={ACTIONS.EDIT}
          existingData={riskCenterData}
          id={id}
        />
      )}

      <div className={styles['single-risk-center__header-actions']}>
        <Button
          text={t('innerLayout.riskCenter.actions.delete')}
          color="danger"
          size="md"
          variant="outlined"
          onClickHandler={() => {
            setIsModalOpen((prev) => ({ ...prev, delete: true }));
            setModalAction(ACTIONS.DELETE);
          }}
          leadingIcon={Xmark}
        />
        <Button
          text={t(
            `innerLayout.riskCenter.actions.${
              riskCenterData?.active ? ACTIONS.DEACTIVATE : ACTIONS.ACTIVATE
            }`
          )}
          size="md"
          onClickHandler={() => {
            setModalAction(
              riskCenterData?.active ? ACTIONS.DEACTIVATE : ACTIONS.ACTIVATE
            );
            setIsModalOpen((prev) => ({
              ...prev,
              activateDeactivate: true,
            }));
          }}
        />
        {type !== RISK_CENTER_TYPES.RULES && (
          <Button
            text={t('innerLayout.riskCenter.actions.edit')}
            size="md"
            onClickHandler={() =>
              setIsModalOpen((prev) => ({ ...prev, edit: true }))
            }
          />
        )}
      </div>
    </div>
  );
};

export default Header;
